import { useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash/debounce";
import { AppDispatch, ActionType } from "../reducer";
import { AddDevcontainersAction } from "../reducer/devcontainer";
import { Devcontainer } from "../types/devcontainer";
import { makeSelectDevcontainer } from "../selectors/ui";
import { writeData } from "../api";
import { getDevcontainer, searchDevcontainers, DevcontainerOption } from "../api/devcontainer";
import { DevcontainerBlock } from "../types/notebook/note/block/devcontainer";
import { useAppIdMap } from "./app";
import { loadKey, loadStatus } from "./loadStatus";
import { requireRecord, useTrackedLoad } from "./loadTracking";
import { useLoadError } from "../slices/ui";

export const useAddDevcontainers = () => {
  const dispatch = useDispatch<AppDispatch>();
  return (devcontainers: Devcontainer[]) => {
    const action: AddDevcontainersAction = { type: ActionType.ADD_DEVCONTAINERS, devcontainers };
    dispatch(action as any);
  };
};
export const useDevcontainer = (devcontainerId: string | undefined) => {
  const select = useMemo(() => makeSelectDevcontainer(devcontainerId), [devcontainerId]);
  const devcontainer = useSelector(select);
  const addDevcontainers = useAddDevcontainers();
  const writeBackend = useMemo(() => debounce((d: Devcontainer) => { writeData(d); }, 500), [devcontainerId]);
  const setDevcontainer = (d: Devcontainer) => { addDevcontainers([d]); writeBackend(d); };
  const setField = (field: "name" | "devcontainer-json", value: string) => {
    if (!devcontainer) return;
    const updated: Devcontainer = {
      ...devcontainer,
      "devcontainer-spec": { ...devcontainer["devcontainer-spec"], [field]: value },
    };
    setDevcontainer(updated);
  };
  return {
    devcontainer,
    setName: (v: string) => { if (v.trim() === "") return; setField("name", v); },
    setJson: (v: string) => setField("devcontainer-json", v),
  };
};
export const useFetchDevcontainer = () => {
  const addDevcontainers = useAddDevcontainers();
  return (block: DevcontainerBlock) =>
    getDevcontainer({ "app-id": block["app-id"], "page-id": block["page-id"], "block-id": block["block-id"] })
      .then((d) => { if (d) addDevcontainers([d]); return d; });
};
// The block's record with an honest status. The fetch runs when the block's three ids are known; Retry re-runs it.
export const useDevcontainerLoad = (block: DevcontainerBlock | undefined) => {
  const fetch = useFetchDevcontainer();
  const trackLoad = useTrackedLoad();
  const { devcontainer } = useDevcontainer(block?.["devcontainer-id"]);
  const key = block ? loadKey("devcontainer", block["block-id"]) : undefined;
  const error = useLoadError(key);
  const retry = () => (block && key ? trackLoad(key, requireRecord(fetch(block), "Devcontainer")) : Promise.resolve(undefined));
  useEffect(() => {
    if (!block || !block["app-id"] || !block["page-id"] || !block["block-id"]) return;
    retry();
  }, [block?.["app-id"], block?.["page-id"], block?.["block-id"]]);
  return { devcontainer, status: loadStatus(devcontainer, error), retry };
};

export const useFetchDevcontainerListener = (block: DevcontainerBlock | undefined) => {
  useDevcontainerLoad(block);
};

export const useDevcontainerSearch = (config: { disabledSearch?: boolean } = {}) => {
  const { disabledSearch } = config;
  const { appId } = useAppIdMap();
  const [searchString, setSearchString] = useState<string>("");
  const [page, setPage] = useState<DevcontainerOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSearch = useMemo(
    () =>
      debounce((s: string) => {
        if (!appId) return;
        setIsLoading(true);
        searchDevcontainers({ "app-id": appId, search: s }).then((rows) => {
          setPage(rows ?? []);
          setIsLoading(false);
        });
      }, 200),
    [appId],
  );

  useEffect(() => {
    if (!disabledSearch) handleSearch(searchString);
  }, [searchString, disabledSearch, handleSearch]);

  return { searchString, setSearchString, isLoading, page };
};
